"use client";

import { useCallback, useState, type ReactNode } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { phCapture } from "@/utils/analytics";
import { EXCHANGE_BOOKS } from "@/lib/book-stake";
import { sportsbookIdToDisplay } from "@/lib/sportsbook-registry";
import { SportsbookLogoMark } from "@/components/sportsbook-logos";
import { SheetShell } from "@/components/odds-screen/live/top-menus";
import { quietClass } from "@/components/fantasy-props/chrome";
import { ComesWithDiamond, DIAMOND_UPGRADE_CLASS, DiamondUnlockAction, useDiamondUpgrade, type DiamondDestination } from "@/components/billing/diamond-upgrade";
import type { WhalesUnlockTrigger } from "@/hooks/use-whales-gate";
import { FlatModal } from "./flat-modal";
import { WhalesLogoMark } from "./whales";

const EXCHANGES = [...EXCHANGE_BOOKS];

const ROW_H = 20;
const ROW_GAP = 4;
const STACK_H = EXCHANGES.length * ROW_H + (EXCHANGES.length - 1) * ROW_GAP;

const BULLETS = [
  "One line from every exchange",
  "Whale $ waiting on each side",
  "Every open whale order, book by book",
];

const FAIR_VALUE_BULLET = "Whales as a source in your fair value";

function exchangeList(): string {
  const names = EXCHANGES.map(sportsbookIdToDisplay);
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

function WhalesConvergence() {
  const mid = STACK_H / 2;
  return (
    <div className="border-b border-surface-line bg-surface-void px-4 pb-4 pt-3">
      <p className="sr-only">{`The order books of ${exchangeList()} combine into one Whales line per side. Example: +117 with $4.8k waiting.`}</p>
      <div aria-hidden="true">
        <p className="mb-2 text-[10px] font-medium uppercase tracking-[0.06em] text-zinc-500">Exchanges</p>
        <div className="flex items-center">
          <ul className="flex flex-shrink-0 flex-col opacity-60" style={{ gap: ROW_GAP }}>
            {EXCHANGES.map((book) => (
              <li key={book} className="flex items-center gap-2" style={{ height: ROW_H }}>
                <span className="flex h-5 w-5 items-center justify-center rounded-md bg-white/[0.06]">
                  <SportsbookLogoMark sportsbook={book} size={16} decorative />
                </span>
                <span className="text-[11px] font-medium text-zinc-400">{sportsbookIdToDisplay(book)}</span>
              </li>
            ))}
          </ul>
          <svg className="mx-2 min-w-6 flex-1 text-white/[0.16]" style={{ height: STACK_H }} viewBox={`0 0 100 ${STACK_H}`} preserveAspectRatio="none" fill="none">
            {EXCHANGES.map((book, index) => {
              const y = index * (ROW_H + ROW_GAP) + ROW_H / 2;
              return <path key={book} d={`M0 ${y} C50 ${y} 50 ${mid} 100 ${mid}`} stroke="currentColor" strokeWidth={1} vectorEffect="non-scaling-stroke" />;
            })}
          </svg>
          <div className="flex flex-shrink-0 flex-col items-start gap-1.5">
            <span className="inline-flex items-center gap-1.5 rounded-md border border-violet-500/35 bg-surface-base px-2 py-1 text-[10px] font-medium text-foreground">
              <span className="h-[5px] w-[5px] rounded-full bg-violet-300" />
              Example
            </span>
            <div className="flex flex-col gap-1 rounded-lg border-2 border-violet-500/45 bg-surface-base px-3 py-2">
              <span className="flex items-center gap-2">
                <WhalesLogoMark size={24} />
                <span className="text-[13px] font-semibold text-foreground">Whales</span>
              </span>
              <span className="flex items-baseline gap-2">
                <span className="data-num text-[13px] font-semibold italic tabular-nums text-foreground">+117</span>
                <span className="data-num whitespace-nowrap text-[10px] font-medium tabular-nums text-zinc-500">$4.8k waiting</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function WhalesPitch({ tall, whalesCanPrice, action, note, onClose }: { tall: boolean; whalesCanPrice: boolean; action: ReactNode; note: string | null; onClose: () => void }) {
  return (
    <>
      <WhalesConvergence />
      <div className="flex flex-col gap-3 px-4 pt-3.5">
        <div>
          <ComesWithDiamond />
          <h2 className="text-[15px] font-semibold text-foreground">See where whale money wants to bet</h2>
        </div>
        <p className="text-[13px] text-zinc-400">Whales stacks the order books of every major exchange into one line per side. It shows the worst price sharp money will still take and how much is waiting there.</p>
        <ul className={cn("grid gap-2", !tall && "grid-cols-2")}>
          {(whalesCanPrice ? [...BULLETS, FAIR_VALUE_BULLET] : BULLETS).map((bullet) => (
            <li key={bullet} className="flex items-start gap-2 text-[13px] text-zinc-300">
              <Check size={12} aria-hidden="true" className="mt-[3px] flex-shrink-0 text-zinc-500" />
              {bullet}
            </li>
          ))}
        </ul>
      </div>
      <div className={cn("mt-4 flex flex-col gap-2 border-t border-surface-line px-4", tall ? "py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]" : "py-3")}>
        <div className={cn("flex", tall ? "flex-col gap-2.5" : "items-center gap-4")}>
          {action}
          {note && <span className={cn("text-[13px] leading-snug text-zinc-300", !tall && "min-w-0 flex-1")}>{note}</span>}
          <button type="button" onClick={onClose} className={cn(quietClass(false, tall), "px-3", tall && "h-11")}>Not now</button>
        </div>
        <p className="text-[11px] text-zinc-500">Whales shows where the money is. You decide what to bet.</p>
      </div>
    </>
  );
}

export function WhalesUpsell({ open, onClose, whalesCanPrice }: { open: boolean; onClose: () => void; whalesCanPrice: boolean }) {
  const tall = useIsMobile() === true;
  const upgrade = useDiamondUpgrade("whales_gate", open);
  const choose = (destination: DiamondDestination) => {
    phCapture("whales_gate_clicked", { destination });
    if (destination === "upgrade_modal") onClose();
  };
  const action = (
    <DiamondUnlockAction upgrade={upgrade} className={cn(DIAMOND_UPGRADE_CLASS, tall ? "h-11 w-full" : "h-9 shrink-0")} onChoose={choose}>Upgrade to Diamond</DiamondUnlockAction>
  );
  return (
    <>
      {tall ? (
        <SheetShell open={open} title="Whales" onClose={onClose} maxHeight="92svh">
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <WhalesPitch tall whalesCanPrice={whalesCanPrice} action={action} note={upgrade.note} onClose={onClose} />
          </div>
        </SheetShell>
      ) : (
        <FlatModal open={open} onClose={onClose} label="Whales, a Diamond feature">
          <WhalesPitch tall={false} whalesCanPrice={whalesCanPrice} action={action} note={upgrade.note} onClose={onClose} />
        </FlatModal>
      )}
      {upgrade.modal}
    </>
  );
}

export function useWhalesUpsell(whalesCanPrice: boolean): { readonly unlock: (trigger: WhalesUnlockTrigger) => void; readonly close: () => void; readonly upsell: ReactNode } {
  const [open, setOpen] = useState(false);
  const unlock = useCallback((trigger: WhalesUnlockTrigger) => {
    phCapture("whales_gate_shown", { trigger });
    setOpen(true);
  }, []);
  const close = useCallback(() => setOpen(false), []);
  return { unlock, close, upsell: <WhalesUpsell open={open} onClose={close} whalesCanPrice={whalesCanPrice} /> };
}
